import { IF, NO, UIColor, UIRoute, UITextView, UITooltip, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHProcurementPublic } from "../../../models/prodModels"
import { RHCore } from "../Core/RHCore"
import { RHProcurementStore } from "../Core/RHProcurementStore"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { CellView } from "../Custom components/CellView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView, RHNumberSorting } from "../Custom components/RH/RHDataView"
import { RHFavoriteColumn } from "../Custom components/RH/RHFavoriteColumn"
import { RHFilterChipBarView } from "../Custom components/RH/RHFilterChipBarView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import { RHFilterSelection } from "../Custom components/RH/RHFilterSelection"
import { RHPublishedProcurementSearch } from "../../../models/PublishedProcurementSearch"
import {
    RHDateRangeCriterion,
    RHFilterCriteria,
    RHFilterPanelView,
    RHRangeCriterion,
    RHTextCriterion
} from "../Custom components/RH/RHFilterPanelView"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDateTimeWithLineBreak } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHColors, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementDeadlineResolutionTrigger } from "../Custom components/RH/Utils_slop/RHProcurementDeadlineResolutionTrigger"
import { RHProcurementLifecyclePresentation } from "../Custom components/RH/Utils_slop/RHProcurementLifecyclePresentation"
import { RHProcurementWinnerStatus } from "../Custom components/RH/Utils_slop/RHProcurementWinnerStatus"
import { labelForRHViewport } from "../Custom components/RH/Utils_slop/RHResponsiveText"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import {
    RHPublishedProcurementsViewTokens,
    RHResponsiveTextTokens
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import {
    OffererExposureFilterKey,
    OffererProcurementExposureFilter
} from "./OffererProcurementExposureFilter"
import {
    OffererCompanyProcurementScope,
    RHCompanyScopedPublishedProcurementRow
} from "./OffererCompanyProcurementScope"
import {
    OffererProcurementSituationFilter,
    OffererProcurementSituationFilterKey
} from "./OffererProcurementSituationFilter"
import {
    RHOffererTenderInterestSettings,
    RHOffererSubmissionOutcome,
    RHProcurementExposureReason,
    RHProcurementInformativeStatus,
    RHProcurementLifecycleState,
    RHPublishedProcurementRow
} from "../SocketClientFunctions"
import { UserMainViewController } from "../UserMain/UserMainViewController"


type PublishedProcurementRow = RHProcurementPublic & {
    unseenNotificationCount: number
    requiresOffererAttention: boolean
    hasSubmittedOfferForCurrentCompany: boolean
    hasViewableOfferDraftForCurrentCompany: boolean
    unseenAnsweredQuestionCount?: number
    offererSubmissionOutcome?: RHOffererSubmissionOutcome
    lifecycleState?: RHProcurementLifecycleState
    offererInformativeStatus?: RHProcurementInformativeStatus
    exposureReasons: RHProcurementExposureReason[]
    // Reconciliation child procurements of this row, oldest first, nested here
    // instead of appearing as their own top-level rows — one row per original
    // hange, with rounds expandable inside it (tree row), matching the
    // procurer's own dashboard. See ProcurementsController._nestReconciliationRoundRows.
    reconciliationRounds?: PublishedProcurementRow[]
}


type PublishedProcurementsViewControllerOptions = {
    showsExposureFilters?: boolean
}


// "interested" uses the current CPV selection; "invited" uses the authoritative
// exposure reason. "lopetatud" covers both contract sub-states.
type OffererQuickFilterKey =
    | OffererExposureFilterKey
    | OffererProcurementSituationFilterKey
    | "favorites"
    | "started"
    | "evaluating"
    | "lopetatud"
    | "awaitingContract"
    | "closedWithoutContract"
    | "inExecution"
    | "executed"


export class PublishedProcurementsViewController extends UIViewController {


    static readonly _interestedFilterLabel = "Mind huvitavad hanked"
    static readonly _interestedFilterInformationIconMarkup =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" ' +
        'fill="none" style="vertical-align:-3px;margin-left:6px" aria-hidden="true" focusable="false">' +
        '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M8 7.25V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '<circle cx="8" cy="4.75" r="0.85" fill="currentColor"/>' +
        '</svg>'
    static readonly _interestedFilterExplanation =
        "Filter näitab avalikke hankeid, mille CPV-koodid kattuvad sinu jaotises „Ettevõtte leitavus " +
        "ja hanketeavitused“ kasutatava CPV-valikuga. Hankele registreerumine seda filtrit ei mõjuta; " +
        "registreerumised leiad filtrist „Olen registreerunud“."
    
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Hanked",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    // ── Primary filter chips (flat, multi-select; wrap via RHFilterChipBarView) ──

    readonly filterAllButton            = PublishedProcurementsViewController._makeFilterChip("Kõik")
    readonly filterInterestedButton     = PublishedProcurementsViewController
        ._makeFilterChip(
            PublishedProcurementsViewController._interestedFilterLabel +
            PublishedProcurementsViewController._interestedFilterInformationIconMarkup
        )
        .configuredWithObject({
            // Keep the native title tooltip empty; UITooltip provides the styled explanation below.
            hoverText: ""
        })
    readonly filterInvitedButton        = PublishedProcurementsViewController._makeFilterChip("Olen kutsutud")
    readonly filterStartedButton        = PublishedProcurementsViewController._makeFilterChip("Alustatud")
    readonly filterEvaluatingButton     = PublishedProcurementsViewController._makeFilterChip("Hindamisel")
    readonly filterFinalizedButton      = PublishedProcurementsViewController._makeFilterChip("Lõpetatud")
    readonly filterAwaitingContractButton =
        PublishedProcurementsViewController._makeFilterChip("Lepingu ootel")
    readonly filterClosedWithoutContractButton =
        PublishedProcurementsViewController._makeFilterChip("Lõpetatud lepinguta")
    readonly filterInExecutionButton    = PublishedProcurementsViewController._makeFilterChip("Täitmisel")
    readonly filterExecutedButton       = PublishedProcurementsViewController._makeFilterChip("Teostatud")

    // Offer situations share the quick-filter union with lifecycle and exposure.
    readonly filterRegisteredButton     = PublishedProcurementsViewController._makeFilterChip("Olen registreerunud")
    readonly filterDraftOfferButton     = PublishedProcurementsViewController._makeFilterChip("Koostamisel")
    readonly filterOfferSubmittedButton = PublishedProcurementsViewController._makeFilterChip("Esitatud")


    // Sits last in the chip row; opens the "Muud filtrid" panel below. Not a
    // primary filter — it carries the funnel + caret icon and a distinct "soft"
    // (open) look, and mirrors the panel's open state via isExpandedDidChange
    // (so the panel's own "Sulge" also closes it).
    readonly moreFiltersButton = PublishedProcurementsViewController._makeFilterChip("Muud filtrid")

    // Favorites select another category in the same quick-filter union.
    readonly filterFavoritesButton = PublishedProcurementsViewController._makeFilterChip("Lemmikud")

    // The wrapping chip bar — owns + lays out all status chips plus "Muud filtrid".
    readonly filterChipBar = new RHFilterChipBarView(this.view.elementID + "FilterChipBar")
        .addedAsSubviewToView(this.view)

    // Reusable collapsible range/date filter panel (Eeldatav maksumus / Avaldatud
    // / Tähtaeg), composed on top of the status tab — same pattern as the procurer.
    readonly filterPanel = new RHFilterPanelView(this.view.elementID + "FilterPanel")
        .configuredWithObject({
            usesExternalToggle: YES,
            fields: [
                {
                    key: "procuringCompanyName", label: "Hankija", kind: "text",
                    autocompleteStringsProvider: () => this._allPublishedProcurements.map(procurement =>
                        procurement.procuringCompany?.name ?? ""
                    )
                },
                { key: "responsibleProcuringUser", label: "Hanke eest vastutav isik", kind: "text" },
                { key: "estimatedCost", label: "Eeldatav maksumus (KM-ta)", kind: "numberRange", allowDecimals: YES },
                { key: "publishedAt", label: "Avaldatud", kind: "dateRange" },
                { key: "deadlineDate", label: "Tähtaeg", kind: "dateRange" }
            ]
        }).addedAsSubviewToView(this.view)

    readonly procurementsDataView = new RHDataView<PublishedProcurementRow>().configuredWithObject({
        additionalSearchTextForDataItem: procurement =>
            RHPublishedProcurementSearch.searchableTextForResponsibleProcuringUserOfProcurement(procurement),
        treeDisclosureColumnKeyPath: "referenceNumber",
        descriptors: [
            RHFavoriteColumn.descriptorWithProcurementIdProvider<PublishedProcurementRow>(
                dataItem => dataItem._id?.toString()
            ),
            CBDataViewCellDescriptorWithObject({
                title: "Viitenumber",
                keyPath: "referenceNumber",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    cellView.text = dataItem.referenceNumber || "-"
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Nimetus",
                keyPath: "title",
                buttonWasPressed: (_cellView, dataItem) => this._openDetailsForProcurement(dataItem),
                initCell: cellView => cellView.configureAsTextLinkWithNormalTextColor(RHColor.textBlack),
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    cellView.text = RHTextSanitizer.escapedHTML(dataItem.title || "Nimetus puudub")
                    // Long titles wrap to (and clamp at) two lines; full title in the tooltip.
                    cellView.configureAsClampedMultilineText()
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Hankija",
                keyPath: "procuringCompany.name",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    cellView.text = dataItem.procuringCompany.name || "-"
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "CPV-kood",
                keyPath: "parts",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    const parts = dataItem.parts || []
                    if (parts.length === 1) {
                        cellView.text = parts[0].CPV || "-"
                    }
                    else if (parts.length > 1) {
                        const cpvCodes = parts.map(p => p.CPV).filter(Boolean)
                        cellView.text = cpvCodes.length > 0
                                        ? `${cpvCodes[0]} (+${cpvCodes.length - 1})`
                                        : `${parts.length} osa`
                    }
                    else {
                        cellView.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.estimatedCost),
                keyPath: RHNumberSorting.valueKeyPath,
                allowSorting: YES,
                dataType: "number",
                sortingInstructions: orderingState => RHNumberSorting.instructions(orderingState),
                // Monetary amounts read best right-aligned (consistent with the procurer table).
                titleLabelAlignment: UITextView.textAlignment.right,
                cellLabelAlignment: UITextView.textAlignment.right,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    if (dataItem.publishEstimatedCostOfProcurement === false) {
                        cellView.text = "—"
                        return
                    }
                    const estimatedCost = RHProcurementStore.totalEstimatedCost(dataItem)
                    if (estimatedCost === undefined) {
                        cellView.text = "-"
                    }
                    else {
                        cellView.text = RHEuroFormatter.formattedAmount(estimatedCost)
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.publicationTime),
                keyPath: "publishedAt",
                allowSorting: YES,
                initialOrderingState: "descending",
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    if (dataItem.publishedAt) {
                        const date = new Date(dataItem.publishedAt)
                        cellView.text = formatRHDateTimeWithLineBreak(date)
                        cellView.titleLabel!.isSingleLine = NO
                    }
                    else {
                        cellView.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Tähtaeg",
                keyPath: "deadlineDate",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    
                    const winnerKind = RHProcurementWinnerStatus.winnerKind(dataItem)
                    
                    if (!dataItem.deadlineDate) {
                        cellView.text = "-"
                        cellView.titleLabel!.textColor = RHColor.textBlack
                        return
                    }
                    
                    const date = new Date(dataItem.deadlineDate)
                    cellView.text = formatRHDateTimeWithLineBreak(date)
                    cellView.titleLabel!.isSingleLine = NO

                    // Closure belongs in the status column; this column always shows the deadline.
                    // "Deadline arrived" (danger) when either the deadline has passed with no winner yet,
                    // or a full winner has
                    // been chosen (a winner can only be chosen after the deadline).
                    const hasDeadlinePassed = date.getTime() < Date.now()
                    const hasDeadlineArrived =
                        (winnerKind === "none" && hasDeadlinePassed) || winnerKind === "full"
                    
                    if (hasDeadlineArrived) {
                        cellView.titleLabel!.textColor = RHColor.textMuted
                    }
                    else {
                        cellView.titleLabel!.textColor = RHColor.textBlack
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Seisund",
                keyPath: "status",
                minimumWidth: RHPublishedProcurementsViewTokens.columnMinimumWidths.status,
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    // Single source of truth shared with the hankija table and the
                    // procurement-details header — see RHProcurementLifecyclePresentation.
                    const presentation = RHProcurementLifecyclePresentation.presentationForOffererRow(dataItem)
                    this._configureStatusCell(
                        cellView,
                        RHProcurementLifecyclePresentation.twoLineCellHTML(
                            presentation.mainText,
                            presentation.secondaryText,
                            presentation.secondaryColor
                        ),
                        presentation.color,
                        presentation.linksToOffers
                    )
                },
                buttonWasPressed: (_cellView, dataItem) => {
                    // Offerer-involvement statuses link straight to the company's
                    // own offer ("Minu pakkumus" = makeOffer tab).
                    if (!RHProcurementLifecyclePresentation.presentationForOffererRow(dataItem).linksToOffers) {
                        return
                    }
                    const backToRouteComponentName =
                        this.backToRouteComponentName ?? PublishedProcurementsViewController.routeComponentName
                    UIRoute.currentRoute
                        .routeWithViewControllerComponent(
                            ProcurementDetailsViewController,
                            {
                                id: dataItem._id!.toString(),
                                tab: "makeOffer",
                                backTo: backToRouteComponentName
                            }
                        )
                        .routeByRemovingComponentNamed(backToRouteComponentName)
                        .apply()
                }
            }),
            CBDataViewCellDescriptorWithObject<PublishedProcurementRow, RHTableActionButtonView>({
                title: "Haldus",
                keyPath: "_id",
                minimumWidth: RHPublishedProcurementsViewTokens.columnMinimumWidths.action,
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView(),
                updateCell: (cellView, dataItem) => {
                    cellView.backgroundColor = this._rowBackgroundColor(dataItem)
                    // Lugemata info makes the action title bold. It never uses
                    // an action dot because there is nothing the offerer must
                    // complete merely by receiving this update.
                    const procurementId = dataItem._id?.toString() ?? ""
                    const hasAnsweredQuestion = (dataItem.unseenAnsweredQuestionCount ?? 0) > 0
                    const hasOfferUpdate = (dataItem.requiresOffererAttention ?? NO) === YES
                    const hasUnreadProcurementNotification =
                        (dataItem.unseenNotificationCount ?? 0) > 0
                    const hasUnreadMessage =
                        RHCore.sharedInstance.notificationStore.offerMessageCount(procurementId) > 0
                    let tooltip = "Hankega seotud lugemata teavitus."
                    if (hasUnreadMessage) {
                        tooltip = "Lugemata sõnum."
                    }
                    if (hasOfferUpdate) {
                        tooltip = "Pakkumusega seotud teavitus."
                    }
                    else if (hasAnsweredQuestion) {
                        tooltip = "Hankija vastas küsimusele."
                    }
                    cellView.customView.text = "Vaata"
                    RHNotificationBadge.applyUnreadEmphasis(
                        cellView.customView.button.titleLabel,
                        hasUnreadProcurementNotification || hasUnreadMessage,
                        tooltip
                    )
                    cellView.customView.buttonWasPressed = () => {
                        this._openDetailsForProcurement(dataItem)
                    }
                }
            })
        ]
    }).addedAsSubviewToView(this.view)
    
    readonly bottomSpacerView = new UIView().addedAsSubviewToView(this.view)

    _openDetailsForProcurement(procurement: PublishedProcurementRow): void {
        const procurementId = procurement._id?.toString()
        if (!procurementId) {
            return
        }
        const backToRouteComponentName =
            this.backToRouteComponentName ?? PublishedProcurementsViewController.routeComponentName
        UIRoute.currentRoute
            .routeWithViewControllerComponent(
                ProcurementDetailsViewController,
                { id: procurementId, backTo: backToRouteComponentName }
            )
            .routeByRemovingComponentNamed(backToRouteComponentName)
            .apply()
    }

    // When hosted under offerer `user_main`, opening a procurement should return
    // to that host instead of the standalone "published_procurements" route.
    backToRouteComponentName?: string

    // Standalone keeps the page title. Hosts may hide it if their own tab or
    // heading already names the section.
    showsPageTitle = YES
    
    _allPublishedProcurements: PublishedProcurementRow[] = []
    // Empty selection means that "Kõik" is active.
    readonly _quickFilters = new RHFilterSelection<OffererQuickFilterKey>()
    _filterCriteria: RHFilterCriteria = {}
    _chipCountUpdateGeneration = 0
    _showsExposureFilters = NO
    _effectiveTenderInterestCPVCodes = new Set<string>()

    _loadHasFailed = NO
    _loadGeneration = 0

    // Periodic repaint so the "Tähtajani …" deadline countdown keeps advancing
    // while this tab is open — started in viewWillAppear, stopped in viewWillDisappear.
    static readonly _deadlineCountdownTickIntervalMilliseconds = 30_000
    _deadlineCountdownTickTimer?: ReturnType<typeof setInterval>
    readonly _deadlineResolutionTrigger = new RHProcurementDeadlineResolutionTrigger()

    constructor(view: UIView, options: PublishedProcurementsViewControllerOptions = {}) {
        super(view)
        this._showsExposureFilters = options.showsExposureFilters ?? NO
        
        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        
        this._applyTableColumnLayout()

        // The chip bar owns the chips (they are its subviews); the controller keeps
        // the offerer-specific selection + count logic. The bar wraps them across
        // rows so the row never overflows the page.
        this.filterChipBar.chips = this._visibleFilterButtons()
        this.filterInterestedButton.viewHTMLElement.setAttribute(
            "aria-description",
            PublishedProcurementsViewController._interestedFilterExplanation
        )
        UITooltip.sharedInstance.attach(
            this.filterInterestedButton,
            PublishedProcurementsViewController._interestedFilterExplanation
        )

        // The panel is the single source of truth for open/closed; the chip only
        // requests a toggle and mirrors the resulting state. The funnel + caret
        // label and the "soft" open look come from the chip itself.
        this.moreFiltersButton.titleLabel.text = PublishedProcurementsViewController._moreFiltersLabelMarkup(NO)
        this.moreFiltersButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.filterPanel.isExpanded = NO == this.filterPanel.isExpanded
        }
        this.filterPanel.isExpandedDidChange = (expanded) => {
            this.moreFiltersButton.chipOpen = expanded
            this.moreFiltersButton.titleLabel.text =
                PublishedProcurementsViewController._moreFiltersLabelMarkup(expanded)
            this.filterChipBar.setNeedsLayout()
        }
        this.filterPanel.filterCriteriaDidChange = (criteria) => {
            this._filterCriteria = criteria
            this.moreFiltersButton.chipActive = Object.keys(criteria).length > 0
            this._applyFilters()
        }

        // Every primary chip is an independent OR-facet. "Kõik" clears them.
        this.filterAllButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._clearQuickFilters()
        }
        for (const chip of this._filterChipDefinitions()) {
            chip.button.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                this._toggleFilter(chip.key)
            }
        }
        this._updateChipsAppearance()

        this.procurementsDataView.didUpdateTableAfterSearchTextChange = () => {
            void this._updateChipCounts()
            this._updateNoDataMessage()
        }
    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.favoritesDidChange) {
            this._applyFilters()
        }
    }

    private _applyTableColumnLayout() {
        this.procurementsDataView.tableHeaderView.configureWithObject({
            cellWeights: RHPublishedProcurementsViewTokens.columnWeights,
            intrinsicContentHeight: () => 64
        })
        this.procurementsDataView.tableView.configureWithObject({
            cellWeights: RHPublishedProcurementsViewTokens.columnWeights
        })
        this._configureTableHeaderCells()
        this.procurementsDataView.tableHeaderView.setNeedsLayout()
        this.procurementsDataView.tableView.setNeedsLayout()
    }
    
    private _configureTableHeaderCells() {
        for (const headerCell of this.procurementsDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHPublishedProcurementsViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHPublishedProcurementsViewTokens.multilineHeaderLineHeight
        }
    }
    
    
    static override readonly routeComponentName = "published_procurements"
    static override readonly ParameterIdentifierName = {}
    
    // static readonly requiredApplicationModes: ("procurer" | "offerer")[] = ["offerer"]
    
    override async viewWillAppear() {
        await super.viewWillAppear()
        RHCore.sharedInstance.procurementStore.subscribe(this, () => {
            this._reloadData()
        })
        if (this._showsExposureFilters) {
            RHCore.sharedInstance.offererConfigurationStore.subscribe(this, () => {
                void this._reloadData()
            })
        }
        RHCore.sharedInstance.notificationStore.subscribe(this, (affectedIds) => {
            if (affectedIds.length === 0) {
                // A user-wide count push changes navigation badges only. Row
                // caches are invalidated by the subsequent authoritative
                // acknowledgement push, which carries concrete procurement IDs.
                return
            }
            void this._reloadData()
        })
        // Repaints the visible rows (no re-fetch) so the "Tähtajani …" deadline
        // countdown keeps advancing while the tab stays open, per spec: "loendur
        // uueneb ajas". Every 30s keeps the minute-granularity text from ever
        // drifting more than half a minute stale.
        this._deadlineCountdownTickTimer = setInterval(() => {
            this._requestResolutionForExpiredDeadlineCountdowns()
            this.procurementsDataView.tableView.reloadData()
        }, PublishedProcurementsViewController._deadlineCountdownTickIntervalMilliseconds)
        await this._reloadData()
    }

    override async viewWillDisappear() {
        await super.viewWillDisappear()
        this._loadGeneration += 1
        RHCore.sharedInstance.procurementStore.unsubscribe(this)
        RHCore.sharedInstance.notificationStore.unsubscribe(this)
        RHCore.sharedInstance.offererConfigurationStore.unsubscribe(this)
        clearInterval(this._deadlineCountdownTickTimer)
    }
    
    override async viewDidAppear() {
        await super.viewDidAppear()

        RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
        this.view.setNeedsLayout()
    }
    
    
    private async _reloadData() {
        const loadGeneration = ++this._loadGeneration
        try {
            let procurementsPromise: Promise<RHCompanyScopedPublishedProcurementRow[]>
            let tenderInterestSettingsPromise: Promise<RHOffererTenderInterestSettings | undefined> =
                Promise.resolve(undefined)
            if (this._showsExposureFilters) {
                procurementsPromise = RHCore.sharedInstance.procurementStore.getOffererDashboardProcurements()
                tenderInterestSettingsPromise = RHCore.sharedInstance.offererConfigurationStore
                    .retrieveTenderInterestSettings()
            }
            else {
                procurementsPromise = RHCore.sharedInstance.procurementStore.retrievePublishedProcurements([
                    "published", "evaluation-finalized", "closed-no-winner", "closed-without-contract"
                ]).then(procurements => OffererCompanyProcurementScope.publishedRowsForOffererCompany(
                    procurements, []
                ))
            }
            const [companyScopedProcurements, tenderInterestSettings] = await Promise.all([
                procurementsPromise, tenderInterestSettingsPromise
            ])
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            this._effectiveTenderInterestCPVCodes = new Set(
                tenderInterestSettings?.effectiveCpvCodes ?? []
            )

            this._loadHasFailed = NO
            this._allPublishedProcurements = companyScopedProcurements.map(procurement =>
                this._normalizedProcurement(procurement)
            )
            this._applyFilters()
            this.view.setNeedsLayout()
            this._deadlineResolutionTrigger.reset()
            this._requestResolutionForExpiredDeadlineCountdowns()
        }
        catch (error) {
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            console.error("Error loading published procurements:", error)
            this._loadHasFailed = YES
            this._allPublishedProcurements = []
            this._effectiveTenderInterestCPVCodes = new Set()
            this._applyFilters()
        }
    }

    // Top-level rows only — see the same note on
    // MyOngoingProcurementsViewController._requestResolutionForExpiredDeadlineCountdowns
    // for why reconciliationRounds isn't recursed into here either.
    _requestResolutionForExpiredDeadlineCountdowns() {
        const deadlineCountdowns: { procurementId: string, deadlineDate: Date | string }[] = []
        for (const procurement of this._allPublishedProcurements) {
            const informativeStatus = procurement.offererInformativeStatus
            if (informativeStatus?.kind !== "deadlineCountdown") {
                continue
            }
            const procurementId = procurement._id?.toString()
            if (!procurementId) {
                continue
            }
            deadlineCountdowns.push({ procurementId, deadlineDate: informativeStatus.deadlineDate })
        }
        this._deadlineResolutionTrigger.requestResolutionForExpiredDeadlines(deadlineCountdowns)
    }


    /**
     * Applies the same estimated-cost sort fields and exposure-reason lookup a
     * top-level row gets to a reconciliation round nested under it, and
     * recurses into any further rounds it has of its own, so every depth of
     * the tree sorts and displays consistently.
     */
    _normalizedProcurement(
        procurement: RHCompanyScopedPublishedProcurementRow
    ): PublishedProcurementRow {
        let estimatedCost: number | undefined = undefined
        if (procurement.publishEstimatedCostOfProcurement !== NO) {
            estimatedCost = RHProcurementStore.totalEstimatedCost(procurement)
        }
        return {
            ...procurement,
            ...RHNumberSorting.fields(estimatedCost),
            reconciliationRounds: procurement.reconciliationRounds?.map(round =>
                this._normalizedProcurement(round)
            )
        } as PublishedProcurementRow
    }
    

    _applyFilters() {
        this._updateChipCounts()

        // Primary chips and the "Muud filtrid" panel compose: a row must pass both;
        // the data view's own search box + column sort layer on top automatically.
        // Criteria/apply stays split so the same criteria can be handed to the
        // server unchanged once pagination lands (same pattern as the procurer).
        // Filters apply to top-level rows only — a reconciliation round stays
        // with its parent whenever the parent passes, same as the procurer's
        // own dashboard (see reconciliationRounds on PublishedProcurementRow).
        const filteredProcurements = this._allPublishedProcurements.filter(procurement =>
            this._quickFilters.doesValueMatchSelection(procurement, (value, key) =>
                this._procurementMatchesQuickFilterKey(value, key)
            ) &&
            this._procurementMatchesFilterCriteria(procurement)
        )
        // Plain `.data =` (not setTreeData) — a reconciliation round is not
        // shown as its own row here, expandable or otherwise; it stays
        // reachable from the root's own detail/evaluation page instead.
        this.procurementsDataView.data = filteredProcurements
        this._updateNoDataMessage()
        this.view.setNeedsLayout()
    }


    _procurementMatchesQuickFilterKey(
        procurement: PublishedProcurementRow,
        key: OffererQuickFilterKey
    ): boolean {
        if (key === "favorites") {
            const procurementId = procurement._id?.toString()
            return !!procurementId && RHCore.sharedInstance.isProcurementFavorite(procurementId)
        }
        if (key === "registeredOnly" || key === "draftOffer" || key === "submitted") {
            return OffererProcurementSituationFilter.doesProcurementMatchFilter(procurement, key)
        }
        if (key === "interested" || key === "invited") {
            return OffererProcurementExposureFilter.doesProcurementMatchOffererExposureFilter(
                procurement,
                key,
                this._effectiveTenderInterestCPVCodes
            )
        }
        const matchingStates = PublishedProcurementsViewController._lifecycleStatesForOffererFilterKey(key)
        return procurement.lifecycleState !== undefined && matchingStates.includes(procurement.lifecycleState)
    }


    /** The lifecycle states a chip key matches — "lopetatud" is the broad chip covering both contract sub-states. */
    static _lifecycleStatesForOffererFilterKey(
        key: Exclude<OffererQuickFilterKey, OffererExposureFilterKey | OffererProcurementSituationFilterKey | "favorites">
    ): RHProcurementLifecycleState[] {
        if (key === "lopetatud") {
            return ["awaitingContract", "closedWithoutContract"]
        }
        return [key]
    }


    // ── Multi-select chip state ────────────────────────────────────────────────

    _filterChipDefinitions(): {
        button: RHFilterChipButton,
        key: OffererQuickFilterKey,
        baseLabel: string
    }[] {
        const definitions: {
            button: RHFilterChipButton,
            key: OffererQuickFilterKey,
            baseLabel: string
        }[] = [
            { button: this.filterStartedButton, key: "started", baseLabel: "Alustatud" },
            { button: this.filterEvaluatingButton, key: "evaluating", baseLabel: "Hindamisel" },
            { button: this.filterFinalizedButton, key: "lopetatud", baseLabel: "Lõpetatud" },
            { button: this.filterAwaitingContractButton, key: "awaitingContract", baseLabel: "Lepingu ootel" },
            {
                button: this.filterClosedWithoutContractButton,
                key: "closedWithoutContract",
                baseLabel: "Lõpetatud lepinguta"
            },
            { button: this.filterInExecutionButton, key: "inExecution", baseLabel: "Täitmisel" },
            { button: this.filterExecutedButton, key: "executed", baseLabel: "Teostatud" }
        ]
        if (this._showsExposureFilters) {
            definitions.push(
                { button: this.filterRegisteredButton, key: "registeredOnly", baseLabel: "Olen registreerunud" },
                { button: this.filterDraftOfferButton, key: "draftOffer", baseLabel: "Koostamisel" },
                { button: this.filterOfferSubmittedButton, key: "submitted", baseLabel: "Esitatud" },
                { button: this.filterInvitedButton, key: "invited", baseLabel: "Olen kutsutud" },
                {
                    button: this.filterInterestedButton,
                    key: "interested",
                    baseLabel: PublishedProcurementsViewController._interestedFilterLabel
                }
            )
        }
        definitions.push({ button: this.filterFavoritesButton, key: "favorites", baseLabel: "Lemmikud" })
        return definitions
    }


    _toggleFilter(key: OffererQuickFilterKey) {
        this._quickFilters.toggleKey(key)
        this._updateChipsAppearance()
        this._applyFilters()
    }


    _clearQuickFilters() {
        this._quickFilters.selectedKeys.clear()
        this._updateChipsAppearance()
        this._applyFilters()
    }


    // Active chips carry the orange offerer-mode accent; the rest stay ghost.
    // "Kõik" is the active chip exactly when nothing else is selected.
    _updateChipsAppearance() {
        this.filterAllButton.chipActive = this._quickFilters.selectedKeys.size === 0
        for (const chip of this._filterChipDefinitions()) {
            chip.button.chipActive = this._quickFilters.selectedKeys.has(chip.key)
        }
    }


    async _updateChipCounts() {
        const updateGeneration = ++this._chipCountUpdateGeneration
        let procurementsMatchingCriteria = this._allPublishedProcurements.filter(
            procurement => this._procurementMatchesFilterCriteria(procurement)
        )
        if (this.procurementsDataView.searchTextField.text.trim().length > 0 ||
            this.procurementsDataView.tableHeaderView.columnFilterCriteria.length > 0) {
            procurementsMatchingCriteria = await this.procurementsDataView.dataByApplyingFiltersToData(
                procurementsMatchingCriteria
            )
            if (updateGeneration !== this._chipCountUpdateGeneration) {
                return
            }
        }
        this.filterAllButton.titleLabel.text = this._chipLabelWithCount("Kõik", procurementsMatchingCriteria.length)
        for (const chip of this._filterChipDefinitions()) {
            const selection = this._quickFilters.selectionForCountingKey(chip.key)
            const count = procurementsMatchingCriteria.filter(procurement => selection.doesValueMatchSelection(
                procurement, (value, key) => this._procurementMatchesQuickFilterKey(value, key)
            )).length
            let chipLabel = this._chipLabelWithCount(chip.baseLabel, count)
            if (chip.key === "interested") {
                chipLabel += PublishedProcurementsViewController._interestedFilterInformationIconMarkup
            }
            chip.button.titleLabel.text = chipLabel
        }
        // The labels (and therefore chip widths) changed → the bar must re-wrap.
        this.filterChipBar.setNeedsLayout()
        this.view.setNeedsLayout()
    }


    // Count appended as PLAIN text ("Avaldatud · 12"). It must not be wrapped in
    // an HTML span: UITextMeasurement treats span-only markup as plain text and
    // would measure the literal tag characters, hugely inflating the chip width
    // (the bar sizes each chip from its label's intrinsic width).
    _chipLabelWithCount(label: string, count: number): string {
        return `${label} · ${count}`
    }


    _procurementMatchesFilterCriteria(procurement: PublishedProcurementRow): boolean {
        const criteria = this._filterCriteria
        if (!RHFilterPanelView.doesTextMatchCriterion(
            RHPublishedProcurementSearch.searchableTextForResponsibleProcuringUserOfProcurement(procurement),
            criteria.responsibleProcuringUser as RHTextCriterion
        )) {
            return NO
        }
        if (!RHFilterPanelView.doesTextMatchCriterion(
            procurement.procuringCompany?.name ?? "",
            criteria.procuringCompanyName as RHTextCriterion
        )) {
            return NO
        }

        const parts = procurement.parts ?? []
        const costs = parts
            .map(part => part?.estimatedCost)
            .filter((cost): cost is number => typeof cost === "number")
        const totalCost = costs.reduce((sum, cost) => sum + cost, 0)
        if (!RHFilterPanelView.matchesNumberRange(totalCost, criteria.estimatedCost as RHRangeCriterion)) {
            return NO
        }

        let publishedAt: Date | undefined = undefined
        if (procurement.publishedAt) {
            publishedAt = new Date(procurement.publishedAt)
        }
        if (!RHFilterPanelView.matchesDateRange(publishedAt, criteria.publishedAt as RHDateRangeCriterion)) {
            return NO
        }

        let deadlineDate: Date | undefined = undefined
        if (procurement.deadlineDate) {
            deadlineDate = new Date(procurement.deadlineDate)
        }
        if (!RHFilterPanelView.matchesDateRange(deadlineDate, criteria.deadlineDate as RHDateRangeCriterion)) {
            return NO
        }

        return YES
    }


    _visibleFilterButtons() {
        return [
            this.filterAllButton,
            ...this._filterChipDefinitions().map(definition => definition.button),
            this.moreFiltersButton
        ]
    }


    static _makeFilterChip(title: string): RHFilterChipButton {
        // The chip owns all of its styling (flat surface, ghost/active/soft modes);
        // the factory only needs to seed the label. The owner drives active/open.
        return new RHFilterChipButton().configuredWithObject({
            titleLabel: { text: title }
        })
    }


    // "Muud filtrid" label: funnel icon + text + a caret that flips when its panel
    // is open. The chip's titleLabel renders this markup as HTML, so the inline
    // SVGs inherit the chip's text colour via currentColor.
    static _moreFiltersLabelMarkup(expanded: boolean): string {
        let caret = RHBadges.filterChip.caretCollapsedMarkup
        if (expanded) {
            caret = RHBadges.filterChip.caretExpandedMarkup
        }
        return RHBadges.filterChip.funnelIconMarkup + "Muud filtrid" + caret
    }


    /**
     * Rows are no longer tinted: the registered-first ordering + green row tint
     * was removed (procurements now read chronologically by publication time, and
     * the registration state is shown only via the "Hankele registreeritud"
     * status). Kept as a single neutral hook so every cell still binds a colour.
     */
    private _rowBackgroundColor(_dataItem: RHProcurementPublic): UIColor {
        return UIColor.transparentColor
    }


    /**
     * Renders the "Seisund" cell — mirrors the procurer's _configureStatusCell so
     * an actionable status is clickable and a plain one is static. `statusText`
     * is the two-line bold-state/italic-informative-status HTML markup from
     * RHProcurementLifecyclePresentation.twoLineCellHTML. `isStatusAction` links
     * to the offerer's own offer.
     */
    _configureStatusCell(
        cellView: CellView<PublishedProcurementRow>,
        statusText: string,
        statusColor: UIColor,
        isStatusAction: boolean
    ) {
        cellView.text = statusText
        if (isStatusAction) {
            // statusText is HTML markup — hoverText is a plain tooltip, so strip the markup.
            cellView.hoverText = RHTextSanitizer.plainText(statusText)
        }
        else {
            cellView.hoverText = undefined
        }
        cellView.titleLabel!.textAlignment = UITextView.textAlignment.center
        cellView.titleLabel!.textColor = statusColor
        // Let the two-line status (main state + informative status) wrap and show
        // in full instead of truncating.
        cellView.titleLabel!.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })

        if (!cellView.buttonView) {
            return
        }

        cellView.buttonView.userInteractionEnabled = isStatusAction
        if (isStatusAction) {
            cellView.buttonView.style.cursor = "pointer"
        }
        else {
            cellView.buttonView.style.cursor = "default"
        }
        cellView.buttonView.colors.titleLabel = {
            normal: statusColor,
            hovered: statusColor,
            focused: statusColor,
            highlighted: statusColor,
            selected: statusColor
        }
        cellView.buttonView.colors.background = {
            normal: UIColor.transparentColor,
            hovered: UIColor.transparentColor,
            focused: UIColor.transparentColor,
            highlighted: UIColor.transparentColor,
            selected: UIColor.transparentColor
        }
        cellView.buttonView.titleLabel.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })
        cellView.buttonView.titleLabel.textAlignment = UITextView.textAlignment.center
        cellView.buttonView.updateContentForCurrentState()
    }
    
    private _updateNoDataMessage() {
        if (this._loadHasFailed) {
            this.procurementsDataView.noDataMessage = "Andmete laadimine ebaõnnestus."
            return
        }
        
        const hasLoadedProcurements = this._allPublishedProcurements.length > 0
        const hasRowsBeforeSearch = this.procurementsDataView.data.length > 0
        const hasActiveSearch = this.procurementsDataView.searchTextField.text.trim().length > 0
        const hasActiveFilter =
            this._quickFilters.selectedKeys.size > 0 ||
            Object.keys(this._filterCriteria).length > 0
        if (hasLoadedProcurements && !hasRowsBeforeSearch) {
            this.procurementsDataView.noDataMessage = "Filtrile vastavaid hankeid pole"
            return
        }
        if (hasRowsBeforeSearch && hasActiveSearch) {
            this.procurementsDataView.noDataMessage = "Filtrile vastavaid hankeid pole"
            return
        }
        if (hasLoadedProcurements && hasActiveFilter) {
            this.procurementsDataView.noDataMessage = "Filtrile vastavaid hankeid pole"
            return
        }
        this.procurementsDataView.noDataMessage = "Hankeid pole"
    }
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)

        const isDirectPublishedProcurementsRoute = route.componentWithViewController(this.class)
        const isOffererDashboardRoute =
            this._showsExposureFilters &&
            RHCore.sharedInstance.isInOffererMode &&
            route.componentWithViewController(UserMainViewController)
        if (isDirectPublishedProcurementsRoute || isOffererDashboardRoute) {
            RHCore.sharedInstance.notificationStore.markPublishedProcurementsSeen()
        }

        const filterParam = route
            .componentWithName(UserMainViewController.routeComponentName)
            ?.parameters?.[UserMainViewController.ParameterIdentifierName.offererFilter] as OffererQuickFilterKey | undefined

        if (filterParam && this._filterChipDefinitions().some(definition => definition.key === filterParam)) {
            this._quickFilters.selectedKeys.clear()
            this._quickFilters.selectedKeys.add(filterParam)
            this._updateChipsAppearance()
            await this._reloadData()

            await route
                .routeByRemovingParameterInComponent(
                    UserMainViewController.routeComponentName,
                    UserMainViewController.ParameterIdentifierName.offererFilter
                )
                .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
        }
    }

    override layoutViewSubviews() {
        super.layoutViewSubviews()

        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)

        if (!this.showsPageTitle && this.titleLabel.superview === this.view) {
            this.titleLabel.removeFromSuperview()
        }

        bounds
            .IF(this.showsPageTitle)
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding, this.filterChipBar)
            .ELSE()
            .rectangleWithHeight(this.filterChipBar)
            .ENDIF()
            // The chip bar wraps its chips across as many rows as the width needs
            // and reports its own height, so the row can never run off the page.
            .assignedAsFrameOfView(this.filterChipBar)
            .rectangleForNextRow(padding, this.filterPanel)
            .assignedAsFrameOfView(this.filterPanel)
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.procurementsDataView)
            .rectangleForNextRow(padding, this.bottomSpacerView)
            .assignedAsFrameOfView(this.bottomSpacerView)
    }


}
